import { Component, EventEmitter, Input, Output } from '@angular/core';
import { isPoiSensor } from 'src/app/services/operators/voyageradar-credit-actions';
import {
  buildVoyageHistoryGroups,
  formatVoyageDate,
  voyageCoverage,
  VoyageCoverage,
  VoyageHistoryGroup,
  VOYAGE_HISTORY_RECENT,
} from './voyage-refresh-history.model';

/**
 * Voyage Radar "Refresh History" — the recent tail of a sensor's snapshots, and
 * the entry points to everything older.
 *
 * The last of three panel sections over the same archive, each with one job:
 *   ON MAP bar (`app-voyage-onmap-bar`)      — what is drawn right now.
 *   Vessel Types (`app-voyage-vessel-types`) — which of it is visible.
 *   this                                     — what else could be drawn.
 *
 * Both archive-scope actions live here because both act on the record as a
 * whole: replaying it (the header's Playback pill) and browsing it (the
 * footer's handoff to `app-voyage-history-browser`).
 *
 * The row list is capped rather than expandable on purpose: letting a 320px
 * sidebar grow to two hundred rows is what made the panel and the archive
 * overlap in the first place.
 *
 * Purely presentational: it knows nothing about Leaflet, S3 or the API. The
 * host decides what selection means and feeds the result back through
 * `activeRefreshId`, so the three sections can never disagree about state.
 *
 * One instance per sensor — it sits inside the host's `*ngFor` over
 * `collection.sensors`.
 *
 * Change detection is deliberately Default: the host mutates
 * `sensor.refreshHistory` in place (a refresh pushes onto the existing array),
 * so an OnPush child would not be told about a new row.
 */
@Component({
  selector: 'app-voyage-refresh-history',
  templateUrl: './voyage-refresh-history.component.html',
  styleUrls: ['./voyage-refresh-history.component.scss'],
})
export class VoyageRefreshHistoryComponent {
  /** The Voyage Radar sensor whose `refreshHistory` this renders. */
  @Input() sensor: any;
  /** refreshId of the snapshot the host currently has on the map, or null. */
  @Input() activeRefreshId: string | null = null;
  /** refreshId to highlight briefly — set by the host after a snapshot change. */
  @Input() flashRefreshId: string | null = null;
  /** True while snapshot playback is running, so the pill reports rather than
   *  offers to restart. Exiting lives on the ON MAP bar, which narrates it. */
  @Input() playbackActive = false;
  /**
   * True while a replay's frames are being fetched and that wait is worth
   * drawing — the host binds the DELAYED flag, not the honest one, so a window
   * served back out of the step cache never blinks a spinner. The same split the
   * Vessel Tracks section makes between `trackLoading` and `loadingTrackId`.
   */
  @Input() playbackLoading = false;
  /**
   * refreshId of the frame playback currently has on screen, or null.
   *
   * Separate from `activeRefreshId`, which means "this snapshot is the one the
   * user put on the map": a playing frame is neither chosen nor persistent, and
   * marking it with the selected style would leave the list claiming a selection
   * the user never made once the replay ends.
   */
  @Input() playbackRefreshId: string | null = null;

  /** A row was picked. Emits the raw refreshHistory record. The host decides
   *  whether that means "show" or "clear" — re-emitting the active row is how
   *  the toggle-off reaches it. */
  @Output() select = new EventEmitter<any>();
  /** Open the archive. */
  @Output() browseAll = new EventEmitter<void>();
  /** Replay the whole archive on the map. */
  @Output() playAll = new EventEmitter<void>();
  /** A row's download button. Emits the record. */
  @Output() download = new EventEmitter<any>();

  /** Expanded by default — this is the section's primary content, unlike
   *  Vessel Types which opens on request. */
  expanded = true;

  private _cacheKey = '';
  private _groups: VoyageHistoryGroup[] = [];
  private _coverage: VoyageCoverage | null = null;

  get history(): any[] {
    return Array.isArray(this.sensor?.refreshHistory) ? this.sensor.refreshHistory : [];
  }

  /**
   * How many records are actually usable — not the raw array length. Entries
   * with an unparseable `name` or no `refreshId` are dropped before rendering,
   * so counting the raw array could advertise eight snapshots above a list that
   * shows none.
   */
  get count(): number {
    return this.coverage?.total ?? 0;
  }

  /**
   * True when this sensor was tasked on a point rather than an area — resolved
   * exactly as the refresh button and the refund panel resolve it, off the first
   * scene carrying a footprint type, so all three agree about the target shape.
   */
  get isPoi(): boolean {
    return isPoiSensor(this.sensor);
  }

  /**
   * Playback needs two frames to be a timeline: `PlaybackController.play()`
   * refuses below that, and the existing "refresh at least twice" warning says
   * as much. Withhold the pill rather than entering a dead transport.
   *
   * A POINT task never gets the pill, however long its archive grows. Snapshot
   * playback is a flip-book of AOI captures — what moved through the tasked area
   * between two refreshes — and a point task has no area: its refreshes come
   * from /v1/vessels/nearby, a proximity ranking around one coordinate rather
   * than a scene of the same water each time. Replaying those frames animates
   * changes in who happened to be nearest, which is not a movement the map can
   * honestly draw. The rows, the download and the archive browser all still
   * apply — only the replay does not.
   */
  get canPlayback(): boolean {
    return this.count >= 2 && !this.isPoi;
  }

  /** "Browse all 214 · 12 – 28 Jul" — the footer's handoff to the archive. */
  get browseLabel(): string {
    const coverage = this.coverage;
    if (!coverage) return 'Browse snapshots';
    return `Browse all ${coverage.total} · ${formatVoyageDate(coverage.minMs)} – ${formatVoyageDate(coverage.maxMs)}`;
  }

  get coverage(): VoyageCoverage | null {
    this._rebuild();
    return this._coverage;
  }

  /**
   * Day-grouped rows, capped at the recent tail.
   *
   * Memoised alongside the coverage because Angular re-evaluates template
   * bindings on every change-detection pass: returning a fresh array each time
   * would re-render the whole list, and re-normalising the history for every
   * count/label read would walk it several times per pass.
   */
  get groups(): VoyageHistoryGroup[] {
    this._rebuild();
    return this._groups;
  }

  isActive(refreshId: string): boolean {
    return !!this.activeRefreshId && refreshId === this.activeRefreshId;
  }

  /** True for the frame playback is showing right now. */
  isPlayingRow(refreshId: string): boolean {
    return !!this.playbackRefreshId && refreshId === this.playbackRefreshId;
  }

  /**
   * True while the list may not be picked from.
   *
   * Playback owns the map for as long as it runs, and a row click drew a
   * recalled layer straight underneath the frame renderer: paused, the map then
   * held two snapshots at once, and playing, the pick was silently swept away by
   * the next frame. Neither is an outcome the row's tooltip promised. The rows
   * stay visible and keep marking where the replay has got to; only the pick is
   * withheld, and `playTooltip` says where the way out is.
   *
   * Mirrors the Vessel Tracks section, whose rows likewise refuse a pick while a
   * load is in flight rather than starting a second one.
   */
  get locked(): boolean {
    return this.playbackActive || this.playbackLoading;
  }

  /** The header pill's label — it reports the replay as much as it starts one. */
  get playLabel(): string {
    if (this.playbackLoading) return 'Loading';
    return this.playbackActive ? 'Playing' : 'Playback';
  }

  get playTooltip(): string {
    if (this.playbackLoading) return 'Loading the saved refreshes…';
    return this.playbackActive
      ? 'Playing — exit from the ON MAP bar above'
      : 'Replay all saved refreshes on the map';
  }

  /** What a row offers, which is nothing at all while a replay owns the map. */
  rowTooltip(row: { refreshId: string; ago: string }): string {
    if (this.locked) return 'Exit playback to pick a snapshot';
    return this.isActive(row.refreshId)
      ? 'Click to take this snapshot off the map'
      : `Show this snapshot on the map · captured ${row.ago}`;
  }

  /** Row click. Withheld while a replay owns the map — see `locked`. */
  pick(row: { data: any }): void {
    if (this.locked) return;
    this.select.emit(row.data);
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /**
   * The cache key is the newest refreshId as well as the length, not just the
   * length: a refresh both appends to the array and (on some paths) replaces
   * its tail.
   */
  private _rebuild(): void {
    const history = this.history;
    const key = [history.length, history[history.length - 1]?.refreshId].join('|');
    if (key === this._cacheKey) return;

    this._cacheKey = key;
    this._coverage = voyageCoverage(history);
    this._groups = buildVoyageHistoryGroups(history, {
      now: Date.now(),
      limit: VOYAGE_HISTORY_RECENT,
    });
  }
}
